/**
 * 🔥 Firestore Booking Repository
 *
 * Collection: booking_requests
 * (نستخدم نفس الاسم اللي في DB القديم للتوافق)
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
  onSnapshot,
  Query,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

import { db } from '@infrastructure/firebase/config';
import { Booking } from '@domain/entities/Booking';
import type {
  IBookingRepository,
  BookingFilters,
} from '@domain/repositories/IBookingRepository';
import type { BookingRequestStatusType } from '@domain/value-objects/BookingRequestStatus';

const COLLECTION = 'booking_requests';

export class FirestoreBookingRepository implements IBookingRepository {
  private get bookingsRef() {
    return collection(db, COLLECTION);
  }

  async getById(bookingId: string): Promise<Booking | null> {
    const snap = await getDoc(doc(db, COLLECTION, bookingId));
    if (!snap.exists()) return null;
    try {
      return Booking.fromFirestore(snap.id, snap.data());
    } catch {
      return null;
    }
  }

  /** يبني query من الفلاتر (بدون orderBy لتجنّب composite index) */
  private buildQuery(filters: BookingFilters): Query<DocumentData> {
    let q: Query<DocumentData> = this.bookingsRef;

    if (filters.venueId) {
      q = query(q, where('venueId', '==', filters.venueId));
    }
    if (filters.providerId) {
      q = query(q, where('providerId', '==', filters.providerId));
    }
    if (filters.customerId) {
      q = query(q, where('userId', '==', filters.customerId));
    }
    if (filters.status) {
      const statuses = Array.isArray(filters.status)
        ? filters.status
        : [filters.status];
      q = query(q, where('status', 'in', statuses));
    }

    return q;
  }

  /** يحوّل snapshot لـ bookings مرتّبة تنازلياً حسب createdAt (يتجاوز التالف) */
  private docsToBookings(snapshot: QuerySnapshot<DocumentData>): Booking[] {
    return snapshot.docs
      .map((d) => {
        try {
          return Booking.fromFirestore(d.id, d.data());
        } catch (err) {
          console.warn('[BookingRepo] فشل parse حجز:', d.id, err);
          return null;
        }
      })
      .filter((b): b is Booking => b !== null)
      .sort((a, b) => b.props.createdAt.getTime() - a.props.createdAt.getTime());
  }

  async query(filters: BookingFilters): Promise<Booking[]> {
    // ⚠️ ما نضيف orderBy لتجنّب الحاجة لـ composite index
    // نرتب محلياً بعد الجلب
    const snapshot = await getDocs(this.buildQuery(filters));
    return this.docsToBookings(snapshot);
  }

  /**
   * 🔴 Live stream — حجوزات حسب الفلتر (يتحدّث تلقائياً)
   */
  subscribeQuery(
    filters: BookingFilters,
    callback: (bookings: Booking[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    return onSnapshot(
      this.buildQuery(filters),
      (snapshot) => callback(this.docsToBookings(snapshot)),
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[BookingRepo] subscribeQuery error:', err);
        onError?.(err);
      }
    );
  }

  async getByProvider(providerId: string): Promise<Booking[]> {
    return this.query({ providerId });
  }

  async getByVenue(venueId: string): Promise<Booking[]> {
    return this.query({ venueId });
  }

  async save(booking: Booking, isCreate = false): Promise<void> {
    const data = booking.toFirestore();
    // عند الإنشاء: createdAt = serverTimestamp (وقت الخادم الموثوق).
    //   التحديثات لا تلمس createdAt (merge يُبقي القيمة الأصلية).
    if (isCreate) {
      data.createdAt = serverTimestamp() as unknown as string;
      data.updatedAt = serverTimestamp() as unknown as string;
    }
    await setDoc(doc(db, COLLECTION, booking.id), data, { merge: true });
  }

  async countByStatus(
    providerId: string,
    status: BookingRequestStatusType
  ): Promise<number> {
    const q = query(
      this.bookingsRef,
      where('providerId', '==', providerId),
      where('status', '==', status)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }
}

export const bookingRepository = new FirestoreBookingRepository();
